/** Modal accesible con backdrop. Cierra con Esc y clic fuera. */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ModalProps {
  abierto: boolean;
  onCerrar: () => void;
  titulo?: string;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: 'sm' | 'md' | 'lg' | 'xl';
}

const ANCHOS = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' } as const;

export function Modal({ abierto, onCerrar, titulo, children, pie, ancho = 'md' }: ModalProps) {
  const contenido = useRef<HTMLDivElement>(null);
  const botonCerrar = useRef<HTMLButtonElement>(null);
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;
  const tituloId = useId();
  useEffect(() => {
    if (!abierto) return;
    const previo = document.activeElement as HTMLElement | null;
    const controles = () => Array.from(contenido.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? []).filter((el) => el.getClientRects().length > 0);
    // Respeta el autoFocus del contenido; si no hay, evita caer en la X del encabezado.
    if (!contenido.current?.contains(document.activeElement)) {
      (controles().find((el) => el !== botonCerrar.current) ?? botonCerrar.current ?? contenido.current)?.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); cerrar.current(); }
      if (e.key !== 'Tab') return;
      const elementos = controles(); const primero = elementos[0]; const ultimo = elementos[elementos.length - 1];
      if (!primero) { e.preventDefault(); contenido.current?.focus(); }
      else if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo?.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); previo?.focus(); };
  }, [abierto]);

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-2 sm:p-4" role="dialog" aria-modal="true" aria-labelledby={titulo ? tituloId : undefined} aria-label={titulo ? undefined : 'Diálogo'}>
      <div className="absolute inset-0 bg-black/50" onClick={onCerrar} />
      <div
        ref={contenido}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[calc(100dvh-2rem)] min-w-0 w-full flex-col rounded-xl bg-superficie shadow-2xl dark:bg-superficie',
          ANCHOS[ancho],
        )}
      >
        {titulo && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-borde p-4 dark:border-borde">
            <h3 id={tituloId} className="min-w-0 break-words text-lg font-medium">{titulo}</h3>
            <button ref={botonCerrar} onClick={onCerrar} aria-label="Cerrar" className="app-toolbar-button shrink-0">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4">{children}</div>
        {pie && <div className="shrink-0 border-t border-borde p-4 dark:border-borde">{pie}</div>}
      </div>
    </div>
  );
}
